import { Card, Descriptions, Space, Table, Typography } from "antd";
import type { TableColumnsType, TableProps } from "antd";
import { ColumnType } from "antd/lib/table";
import { useIsMobile } from "hooks/useIsMobile";
import { capitalize } from "lodash";

interface ResponsiveTableProps<T extends object> extends TableProps<T> {
  columns: TableColumnsType<T>;
  titleDataIndex?: string;
  titleIcon?: React.ReactNode;
}

function toDescriptions<T extends object>(
  record: T,
  columns: TableColumnsType<T>,
  titleDataIndex?: string,
): React.ReactNode {
  const flat = flattenColumns(columns, titleDataIndex);

  return (
    <Descriptions column={1} size="small" bordered>
      {flat.map((col, i) => {
        const value =
          col.dataIndex != null ? record[col.dataIndex as keyof T] : undefined;
        const rendered = col.render
          ? (col.render(value, record, i) as React.ReactNode)
          : (value as React.ReactNode);

        return (
          <Descriptions.Item key={col.label ?? i} label={col.label}>
            {rendered}
          </Descriptions.Item>
        );
      })}
    </Descriptions>
  );
}

export function ResponsiveTable<T extends object>({
  columns,
  titleDataIndex,
  titleIcon,
  rowClassName,
  ...tableProps
}: ResponsiveTableProps<T>) {
  const { isMobile } = useIsMobile();

  if (!isMobile) {
    return <Table columns={columns} {...tableProps} />;
  }

  const mobileColumns: TableColumnsType<T> = [
    {
      dataIndex: titleDataIndex,
      width: "100%",
      align: "center",
      title: undefined,
      render: (titleValue, record) => {
        const titleNode =
          titleDataIndex != null ? (
            <Space>
              {titleIcon}
              <Typography.Text strong style={{ color: "#1677ff" }}>
                {String(titleValue ?? "")}
              </Typography.Text>
            </Space>
          ) : undefined;

        return (
          <Card
            size="small"
            title={titleNode}
            style={{
              margin: rowClassName ? "10px" : undefined,
            }}
          >
            {toDescriptions(record, columns, titleDataIndex)}
          </Card>
        );
      },
    } as ColumnType<T>,
  ];

  return (
    <Table
      columns={mobileColumns}
      rowClassName={rowClassName}
      {...tableProps}
      showHeader={false}
    />
  );
}

interface FlatColumn<T> {
  label: string;
  dataIndex?: string;
  render?: ColumnType<T>["render"];
}

function flattenColumns<T extends object>(
  columns: TableColumnsType<T>,
  titleDataIndex?: string,
  groupPrefix?: string,
): FlatColumn<T>[] {
  const result: FlatColumn<T>[] = [];

  for (const col of columns) {
    // ColumnGroupType — tiene children
    if ("children" in col && col.children) {
      const prefix = typeof col.title === "string" ? col.title : groupPrefix;
      result.push(
        ...flattenColumns(
          col.children as TableColumnsType<T>,
          titleDataIndex,
          prefix,
        ),
      );
      continue;
    }

    const leaf = col as ColumnType<T>;
    const dataIndex = leaf.dataIndex as string | undefined;
    const title = typeof leaf.title === "string" ? leaf.title : undefined;

    // Omitir la columna del título principal
    if (dataIndex === titleDataIndex) continue;
    // Omitir columnas sin título (no hay label que mostrar)
    if (!title) continue;

    const label = groupPrefix ? `${capitalize(groupPrefix)} · ${title}` : title;

    result.push({ label, dataIndex, render: leaf.render });
  }

  return result;
}
